/**
 * The date and time field, which is `datetime-local` and therefore not an ISO string.
 *
 * `<input type="datetime-local">` reads and writes `YYYY-MM-DDTHH:mm` in the *device's* zone
 * with no offset on it, and everything the app stores is ISO 8601 with one. Converting in
 * both directions is two lines and one time-zone bug, so it is written once here with a test
 * rather than inline on the setup screen.
 *
 * Deliberately no library. A cricket match starts on the phone in the scorer's hand, in the
 * zone that phone is in, which is exactly what `Date` already does.
 */

const pad = (value: number): string => String(value).padStart(2, "0");

/** A `Date` as the input element wants it: local wall-clock time, to the minute. */
export function toLocalInput(when: Date): string {
  return (
    `${when.getFullYear()}-${pad(when.getMonth() + 1)}-${pad(when.getDate())}` +
    `T${pad(when.getHours())}:${pad(when.getMinutes())}`
  );
}

/**
 * What the input holds, as an ISO string — or null when it holds nothing.
 *
 * Null rather than an epoch fallback: the field is optional, and "1970" quietly recorded
 * against a match is worse than no date at all. A value the browser cannot parse is treated
 * the same way, because there is nothing truthful to store.
 */
export function fromLocalInput(value: string): string | null {
  if (value.trim() === "") return null;
  const when = new Date(value);
  return Number.isNaN(when.getTime()) ? null : when.toISOString();
}
